import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useFarliOnMap } from '../utils/farliMapPref';

const FARLI_FACE = "https://customer-assets.emergentagent.com/job_mc-community-1/artifacts/0iwvudcy_905DB496-6C04-454A-A77D-D1FDDD2ED3CA.jpeg";

const STORAGE_KEY = 'farli-position';

const getInitialPosition = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (_e) {}
  return { x: window.innerWidth - 75, y: window.innerHeight - 180 };
};

const FarliFloatingButton = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [farliOnMap] = useFarliOnMap();
  const [pos, setPos] = useState(getInitialPosition);
  const [dragging, setDragging] = useState(false);
  const [hidden, setHidden] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, moved: false });
  const containerRef = useRef(null);

  // Hide Farli when a full-screen modal/overlay is open (event-reel creator, audio picker, replay preview, etc.)
  // Any element with z-index >= 2000 on the page counts as "blocking".
  useEffect(() => {
    const check = () => {
      const modals = document.querySelectorAll(
        '[data-testid="event-reel-creator"], [data-testid="audio-picker-overlay"], [data-testid="public-reel-page"], [data-testid="reels-page"], [data-testid="ride-story-editor"], [data-replay-overlay="ride-replay-player"]',
      );
      setHidden(modals.length > 0);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const clamp = (x, y) => ({
    x: Math.max(0, Math.min(x, window.innerWidth - 60)),
    y: Math.max(60, Math.min(y, window.innerHeight - 60)),
  });

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    dragRef.current = {
      startX: e.clientX || e.touches?.[0]?.clientX || 0,
      startY: e.clientY || e.touches?.[0]?.clientY || 0,
      startPosX: pos.x,
      startPosY: pos.y,
      moved: false,
    };
  };

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const dx = clientX - dragRef.current.startX;
      const dy = clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
      const newPos = clamp(dragRef.current.startPosX + dx, dragRef.current.startPosY + dy);
      setPos(newPos);
    };

    const onUp = () => {
      setDragging(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, pos]);

  const handleClick = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Hide Farli on /farli (already there) and on the map UNLESS the
  // rider has explicitly opted in via Mere → Farli. Default is OFF so
  // the map stays the hero, but riders who want quick AI access can
  // pin Farli to the map from the Mere menu.
  if (location.pathname === '/farli') return null;
  if (location.pathname === '/kort' && !farliOnMap) return null;
  if (hidden) return null;

  return (
    <div
      ref={containerRef}
      className="fixed z-50 touch-none"
      style={{ left: pos.x, top: pos.y, cursor: dragging ? 'grabbing' : 'grab' }}
      onPointerDown={onPointerDown}
      data-testid="farli-floating-container"
    >
      <Link
        to="/farli"
        onClick={handleClick}
        className="block"
        aria-label={t('gen.1e8b310187')}
        data-testid="farli-floating-link"
        draggable={false}
      >
        <div className={`relative w-14 h-14 rounded-full border-[3px] border-emerald-400/60 shadow-2xl shadow-emerald-500/30 overflow-hidden bg-zinc-900 transition-transform ${dragging ? 'scale-110' : 'active:scale-95'}`}>
          <img loading="lazy" src={FARLI_FACE} alt="Farli" className="w-full h-full object-cover pointer-events-none" draggable={false} />
        </div>
        <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-zinc-900" />
      </Link>
    </div>
  );
};

export default FarliFloatingButton;
